/** How the money arrived. The one value that changes what a refund can do. */
export const STANDALONE_CHANNEL = 'terminal_standalone';

/**
 * The provider that means WE took the money and we can send it back.
 *
 * Anything else — `square`, or no provider at all for cash and cheque — is a
 * transaction whose money moved somewhere this CRM cannot reach.
 */
export const BLUESAGE_PROVIDER = 'bluesage';

/**
 * The refund dialog — item 8.
 *
 * Opened from the 3-dot menu on a transaction row. Rewritten 2026-08-27 after
 * the middleware solved `V0543` — it was their missing `auth_code`, never a
 * Valor limitation — so **the card-present special case is gone**. A terminal
 * charge now refunds exactly like a pay-link charge: partial, cumulative,
 * before or after settlement.
 *
 * ## Never gate a refund on terminal state
 *
 * Measured on production with the device deliberately powered off and on: a
 * full refund works either way. Device ON takes the device-void rail
 * (`type: void`, ~25–40 s); device OFF falls to the offset rail
 * (`type: refund`, ~6 s). The device path is an OPTIMISATION, not a
 * requirement. **The dialog therefore shows no terminal messaging at all.**
 *
 * ## The one real remaining gap
 *
 * A sale with `channel: "terminal_standalone"` — run ON the device rather than
 * pushed by us — cannot be partially refunded at ANY amount. `V0573
 * Transaction restricted`; a processor capability boundary, not a middleware
 * defect, proven by single-variable test ($0.50 refused, $0.90 refused, $1.06
 * full approved). Such a transaction defaults to a full refund and says why —
 * but the attempt is **not hard-blocked**, because the middleware deliberately
 * does not pre-empt the processor and blocking client-side would hide the day
 * Valor enables it.
 */
/**
 * The reasons a refund can be given, as YMM defined them on 2026-08-28.
 *
 * Kept beside the state rather than in the component: it is a business rule
 * about how money is accounted for, not a list of labels for one screen.
 */
export const BLUESAGE_REFUND_REASONS = [ 'Damage', 'Billing error', 'Other' ] as const;

export type BlueSageRefundReason = (typeof BLUESAGE_REFUND_REASONS)[number];

/** The category that cannot stand on its own. */
export const REFUND_REASON_NEEDING_DETAIL: BlueSageRefundReason = 'Other';

/**
 * What is actually recorded against the refund.
 *
 * The category and the words are stored as one sentence because that is what a
 * person reads back — `Damage — sofa arm torn in transit`. Deriving it in one
 * place keeps the ledger's format from drifting between the two callers.
 */
export function composeRefundReason(
    category: BlueSageRefundReason | '',
    detail: string,
): string {
    const trimmed = detail.trim();
    if (!category) { return trimmed; }

    return trimmed ? `${ category } — ${ trimmed }` : category;
}

/** Whether this pair is enough to submit. */
export function isRefundReasonComplete(
    category: BlueSageRefundReason | '',
    detail: string,
): boolean {
    if (!category) { return false; }

    return category !== REFUND_REASON_NEEDING_DETAIL || detail.trim().length > 0;
}

export const blueSageRefundDialogInitialState = {
    open: false as boolean,

    /** The transaction being refunded. */
    transactionId: undefined as string | undefined,
    /** What was originally taken, in minor units. */
    originalMinor: 0 as number,
    /**
     * What is still refundable, in minor units.
     *
     * Read from the transaction rather than assumed: after a partial refund the
     * original comes back as `partially_refunded`, which the spec is explicit
     * means *more CAN still be refunded — treat it as a live, refundable sale*.
     * Grouping that with `refunded` would hide money the customer is still owed.
     */
    refundableMinor: 0 as number,
    /** `terminal_standalone` is the one that cannot take a partial. */
    channel: undefined as string | undefined,

    /**
     * The payment type's provider — `bluesage`, `square`, or nothing at all.
     *
     * This decides whether a refund MOVES MONEY or merely records one.
     *
     * A transaction taken on another system, or in cash, has no BlueSage
     * reference to refund against: `executeBlueSageRefund` is not called, and
     * `refundTransaction` posts a refund leg straight to `paid`. That is correct
     * bookkeeping and it is what the CRM has always done — but nothing reaches a
     * card, so a dialog that says *"Refunded $99.00 to ••••4242"* would be
     * telling an operator money went back that never left. It says so instead.
     */
    provider: undefined as string | undefined,
    /** Card last four, for the confirm button: *Refund $1,234.00 to ••••4242*. */
    cardLast4: undefined as string | undefined,

    /**
     * The differential the ORIGINAL sale was charged, in minor units.
     *
     * ⚠ DISPLAY ONLY, exactly like the card price in Take Payment. The amount
     * that reaches the card is grossed up SERVER-side by
     * `grossUpRefundToCard`, from the sale's own recorded differential; this
     * copy exists so the operator can read the customer's figure before they
     * press Confirm, and it changes nothing if it is wrong.
     *
     * 0 — the common case off a location with no dual pricing — makes the quote
     * disappear entirely rather than render "plus $0.00".
     */
    saleDifferentialMinor: 0 as number,

    /**
     * What the card was charged for the sale, in minor units.
     *
     * Display only, and needed for one case the base and the differential
     * cannot answer between them: a FULL refund reverses the entire capture —
     * tip and tax included — so the customer's figure is this, not
     * `base + differential`. `bluesageAmountPaid` is omitted by the settle
     * handler whenever it equals the credited amount, so an absent value means
     * "the same as the base" rather than "unknown".
     */
    salePaidMinor: 0 as number,

    /** The tip on the sale, in minor units. 0 when there was none. */
    saleTipMinor: 0 as number,

    /**
     * On a FULL refund, also give the customer their tip back.
     *
     * **Defaults to false**, which is YMM's decision of 2026-08-29: a tip is the
     * crew's money and the customer's own choice, so refunding it makes the
     * company eat it. A partial has never returned one.
     *
     * ⚠ It is a choice rather than a rule because withholding costs the VOID
     * rail. The middleware only voids when the figure clears the whole capture,
     * so leaving a tip behind means an offset CREDIT against the sale instead of
     * an erased charge — and on a `terminal_standalone` sale it is a partial,
     * which the processor may refuse outright. The dialog says so beside the
     * checkbox rather than letting an operator discover it from a decline.
     */
    refundTip: false as boolean,

    /** Pre-filled to the full refundable amount — the common case is one click. */
    amountMinor: 0 as number,

    /**
     * Why the money is going back. **Required**, and chosen from a fixed list.
     *
     * A free-text box that nobody has to fill in produces a refund ledger of
     * blanks, and the reason is the only record of why a customer was given
     * money back — it is what a franchise owner reads six months later when the
     * numbers are questioned. YMM asked for it on 2026-08-28.
     *
     * The list is short on purpose. Categories people can pick from without
     * thinking are categories that get used honestly; a long list gets
     * mis-picked and a free-text box gets left empty.
     */
    reasonCategory: '' as BlueSageRefundReason | '',

    /**
     * The words beside the category.
     *
     * **Required when the category is `Other`** — "Other" on its own records
     * nothing at all, which is the same as leaving it blank. Optional otherwise,
     * because *Damage* and *Billing error* already say the useful part and
     * forcing a sentence out of someone produces "damage" typed again.
     */
    reasonDetail: '' as string,

    submitting: false as boolean,
    /** Operator-facing, already mapped from `body.code` by the server. */
    error: undefined as string | undefined,

    /**
     * Set when the rail refused a PARTIAL on a standalone charge and handed us
     * the exact full-refund figure. That is what makes the recovery one click.
     */
    fullRefundOnlyMinor: undefined as number | undefined,

    /** The rail that was actually taken, read off the result, never predicted. */
    resultRail: undefined as string | undefined,
    done: false as boolean,

    // Requirement: once sent, the dialog reports what the LEG says, not what the
    // request said. These identify this attempt's leg among the job's.
    knownLegIds: [] as string[],
    awaitsConfirmation: false as boolean,
    confirmationOverdue: false as boolean,
};

export type BlueSageRefundDialogState = typeof blueSageRefundDialogInitialState;
